import { useEffect, useRef } from 'react';

export type StudioPlaybackMode = 'full' | 'range' | 'opening';

/** Wait for each picture and discard loading time instead of skipping unseen frames. */
export function useStudioPlayback(playing: boolean, ready: boolean, progress: number, clockSeconds: number,
  end: number, onProgress: (progress: number) => void, onPause: () => void, flush: () => boolean) {
  const current = useRef({ ready, progress, end, onProgress, onPause });
  current.current = { ready, progress, end, onProgress, onPause };
  useEffect(() => {
    if (!playing) { flush(); return; }
    let frame = 0;
    let previous: number | undefined;
    let blocked = false;
    const advance = (timestamp: number) => {
      const next = current.current;
      if (document.hidden) { previous = undefined; blocked = false; }
      else if (!next.ready) blocked = true;
      else if (next.progress >= next.end) { next.onPause(); return; }
      else {
        // A completed slow frame can advance immediately. Discard its loading
        // time, keeping the established elapsed-time pace for ready playback.
        if (blocked) { previous = timestamp - 1_000 / 30; blocked = false; }
        if (previous === undefined) previous = timestamp;
        else if (timestamp - previous >= 1_000 / 30 - .001) {
          const elapsed = Math.min(timestamp - previous, 100) / 1_000;
          previous = timestamp;
          next.ready = false;
          next.onProgress(Math.min(next.end, next.progress + elapsed / clockSeconds));
        }
      }
      frame = requestAnimationFrame(advance);
    };
    frame = requestAnimationFrame(advance);
    return () => cancelAnimationFrame(frame);
  }, [playing, clockSeconds, flush]);
}
